import {FC, useEffect, useMemo, useRef, useState} from 'react'
import {getSalesAgents, rebuildSalesAgents, getRebuildStatus, analyzeAgent, updateAgent, addCity, renameCity, deleteCity} from './_requests'

const NO_CITY = 'ללא עיר'

interface Agent {
  _id: string
  company_name: string
  city: string
  phone?: string
  agent_name?: string
  excluded?: boolean
  ai_confidence?: number
  ai_city?: string
  account?: {_id: string; first_name?: string; last_name?: string; email?: string; phone?: string; trial_period_end?: string}
}

const SalesAgentsPage: FC = () => {
  const [agents, setAgents] = useState<Agent[]>([])
  const [cities, setCities] = useState<string[]>([])
  const [curatedCities, setCuratedCities] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [rebuild, setRebuild] = useState<any>({running: false})
  const [aiAvailable, setAiAvailable] = useState(true)
  const [stats, setStats] = useState<any>({})
  const [search, setSearch] = useState('')
  const [newCity, setNewCity] = useState('')
  const [analyzing, setAnalyzing] = useState<string | null>(null)
  const pollRef = useRef<any>(null)

  const load = async () => {
    try {
      const res = await getSalesAgents()
      setAgents(res.data.agents || [])
      setCities(res.data.cities || [])
      setCuratedCities(res.data.curated_cities || [])
      setRebuild(res.data.rebuild || {running: false})
      setAiAvailable(res.data.ai_available)
      setStats(res.data.stats || {})
    } catch (e) { console.error(e) }
    setLoading(false)
  }
  useEffect(() => { load() }, [])

  // Poll rebuild progress while a build is running.
  useEffect(() => {
    if (rebuild?.running && !pollRef.current) {
      pollRef.current = setInterval(async () => {
        try {
          const r = await getRebuildStatus()
          setRebuild(r.data.rebuild)
          if (!r.data.rebuild?.running) { clearInterval(pollRef.current); pollRef.current = null; load() }
        } catch (_) {}
      }, 3000)
    }
    return () => { if (pollRef.current && !rebuild?.running) { clearInterval(pollRef.current); pollRef.current = null } }
  }, [rebuild?.running])

  const startRebuild = async () => {
    if (!window.confirm('לנתח מחדש את כל חשבונות הניסיון הפעילים באמצעות AI? פעולה זו עשויה לקחת מספר דקות.')) return
    try {
      await rebuildSalesAgents()
      setRebuild({running: true, total: 0, done: 0})
    } catch (e: any) { alert(e?.response?.data?.error || 'שגיאה') }
  }

  const patch = (id: string, p: Partial<Agent>) => setAgents(prev => prev.map(a => a._id === id ? {...a, ...p} : a))

  const saveField = async (id: string, data: any) => {
    try { await updateAgent(id, data) } catch (e: any) { alert(e?.response?.data?.error || 'שמירה נכשלה'); load() }
  }
  const exclude = async (a: Agent) => {
    if (!window.confirm(`להסיר את "${a.company_name}" מהרשימה?`)) return
    setAgents(prev => prev.filter(x => x._id !== a._id))
    try { await updateAgent(a._id, {excluded: true}) } catch (e: any) { alert('שגיאה'); load() }
  }
  const moveCity = async (a: Agent, city: string) => {
    patch(a._id, {city})
    await saveField(a._id, {city})
  }
  const reAnalyze = async (a: Agent) => {
    setAnalyzing(a._id)
    try { const r = await analyzeAgent(a._id); if (r.data?.agent) patch(a._id, {city: r.data.agent.city, company_name: r.data.agent.company_name, is_real_estate: r.data.agent.is_real_estate} as any); if (r.data?.agent?.is_real_estate === false) setAgents(prev => prev.filter(x => x._id !== a._id)) }
    catch (e: any) { alert(e?.response?.data?.error || 'שגיאה') }
    setAnalyzing(null)
  }

  const doAddCity = async () => {
    const name = newCity.trim()
    if (!name) return
    try { await addCity(name); setNewCity(''); load() } catch (e: any) { alert(e?.response?.data?.error || 'שגיאה') }
  }
  const doRenameCity = async (name: string) => {
    const cur = curatedCities.find(c => c.name === name)
    const next = window.prompt('שם חדש לעיר:', name)
    if (!next || next.trim() === name) return
    try {
      if (cur) await renameCity(cur._id, next.trim())
      else await addCity(next.trim()) // city only existed on rows — create + agents reassign on next rename via name match handled server-side only for curated; do a manual move
      load()
    } catch (e: any) { alert(e?.response?.data?.error || 'שגיאה') }
  }
  const doDeleteCity = async (name: string) => {
    const cur = curatedCities.find(c => c.name === name)
    if (!cur) { alert('ניתן להסיר רק ערים שנוספו ידנית. כדי לרוקן עיר, העבירו את הלקוחות לעיר אחרת.'); return }
    if (!window.confirm(`להסיר את העיר "${name}"? הלקוחות בה יועברו ל"${NO_CITY}".`)) return
    try { await deleteCity(cur._id); load() } catch (e: any) { alert(e?.response?.data?.error || 'שגיאה') }
  }

  // Group agents by city (respecting search), then order cities.
  const grouped = useMemo(() => {
    const q = search.trim().toLowerCase()
    const filtered = q ? agents.filter(a => [a.company_name, a.agent_name, a.phone, a.account?.email, a.city].filter(Boolean).join(' ').toLowerCase().includes(q)) : agents
    const map: Record<string, Agent[]> = {}
    filtered.forEach(a => { const c = (a.city || '').trim() || NO_CITY; (map[c] = map[c] || []).push(a) })
    // Ensure curated cities show even if empty.
    cities.forEach(c => { const k = (c || '').trim() || NO_CITY; if (!map[k]) map[k] = [] })
    const names = Object.keys(map).sort((a, b) => {
      if (a === NO_CITY) return 1; if (b === NO_CITY) return -1
      return map[b].length - map[a].length || a.localeCompare(b, 'he')
    })
    return names.map(name => ({name, rows: map[name]}))
  }, [agents, cities, search])

  const allCityOptions = useMemo(() => Array.from(new Set([...cities, ...agents.map(a => a.city).filter(Boolean)])).filter(Boolean).sort((a, b) => a.localeCompare(b, 'he')), [cities, agents])

  return (
    <div style={{padding: '24px', direction: 'rtl', maxWidth: 1100, margin: '0 auto'}}>
      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 20}}>
        <div>
          <h2 style={{fontSize: 22, fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: 10}}>
            <i className="bi bi-buildings" style={{color: '#3699ff'}} />לקוחות נדל״ן לפי עיר
          </h2>
          <p style={{color: '#a1a5b7', fontSize: 13, marginTop: 4}}>סוכני נדל״ן בניסיון פעיל — לשימוש בסגירת לקוחות חדשים. {stats.shown != null && `מוצגים ${stats.shown}`}</p>
        </div>
        <button onClick={startRebuild} disabled={rebuild?.running}
          style={{padding: '9px 16px', borderRadius: 8, border: 'none', background: rebuild?.running ? '#e8f4fd' : '#3699ff', color: rebuild?.running ? '#3699ff' : '#fff', fontWeight: 700, fontSize: 13, cursor: rebuild?.running ? 'default' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8}}>
          {rebuild?.running
            ? <><span className="spinner-border spinner-border-sm" />מנתח… {rebuild.done || 0}/{rebuild.total || '?'}</>
            : <><i className="bi bi-robot" />נתח חשבונות (AI)</>}
        </button>
      </div>

      {!aiAvailable && <div style={{background: '#fff8dd', border: '1px solid #ffc700', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#b9921a'}}><i className="bi bi-exclamation-triangle me-2" />שירות ה-AI אינו מוגדר (ANTHROPIC_API_KEY) — הסיווג מתבסס על היוריסטיקות בלבד.</div>}

      {/* Toolbar: search + add city */}
      <div style={{display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16}}>
        <div style={{position: 'relative', flex: '1 1 240px', minWidth: 200}}>
          <i className="bi bi-search" style={{position: 'absolute', insetInlineStart: 12, top: '50%', transform: 'translateY(-50%)', color: '#a1a5b7', fontSize: 13}} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="חפש חברה / סוכן / טלפון / עיר…"
            style={{width: '100%', padding: '9px 12px 9px 34px', border: '1px solid #e4e6ef', borderRadius: 10, fontSize: 14, outline: 'none', direction: 'rtl'}} />
        </div>
        <div style={{display: 'flex', gap: 6}}>
          <input value={newCity} onChange={e => setNewCity(e.target.value)} onKeyDown={e => e.key === 'Enter' && doAddCity()} placeholder="הוסף עיר…"
            style={{padding: '9px 12px', border: '1px solid #e4e6ef', borderRadius: 10, fontSize: 14, width: 160, direction: 'rtl'}} />
          <button onClick={doAddCity} style={{padding: '9px 14px', borderRadius: 10, border: 'none', background: '#50cd89', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer'}}>
            <i className="bi bi-plus-lg me-1" />הוסף עיר
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{textAlign: 'center', padding: 60}}><span className="spinner-border text-primary" /></div>
      ) : grouped.length === 0 ? (
        <div style={{textAlign: 'center', padding: 60, color: '#a1a5b7'}}>
          <i className="bi bi-buildings" style={{fontSize: 40, display: 'block', marginBottom: 10, opacity: 0.4}} />
          אין נתונים עדיין. לחץ "נתח חשבונות (AI)" כדי לבנות את הרשימה.
        </div>
      ) : (
        <div style={{display: 'flex', flexDirection: 'column', gap: 16}}>
          {grouped.map(group => (
            <div key={group.name} style={{background: '#fff', borderRadius: 12, border: '1px solid #e4e6ef', overflow: 'hidden'}}>
              <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 18px', background: '#f8fafc', borderBottom: group.rows.length ? '1px solid #eef1f5' : 'none'}}>
                <div style={{fontWeight: 700, fontSize: 15, color: '#1e1e2d', display: 'flex', alignItems: 'center', gap: 8}}>
                  <i className="bi bi-geo-alt-fill" style={{color: '#3699ff'}} />{group.name}
                  <span style={{background: '#e8f4fd', color: '#3699ff', borderRadius: 99, fontSize: 11, fontWeight: 700, padding: '2px 9px'}}>{group.rows.length}</span>
                </div>
                {group.name !== NO_CITY && (
                  <div style={{display: 'flex', gap: 4}}>
                    <button onClick={() => doRenameCity(group.name)} title="שנה שם" style={{border: 'none', background: 'transparent', color: '#7e8299', cursor: 'pointer', padding: 4}}><i className="bi bi-pencil" /></button>
                    {curatedCities.some(c => c.name === group.name) && (
                      <button onClick={() => doDeleteCity(group.name)} title="הסר עיר" style={{border: 'none', background: 'transparent', color: '#f64e60', cursor: 'pointer', padding: 4}}><i className="bi bi-trash" /></button>
                    )}
                  </div>
                )}
              </div>
              {group.rows.length === 0 ? (
                <div style={{padding: '14px 18px', color: '#b5b5c3', fontSize: 13}}>אין לקוחות בעיר זו — העבירו לקוחות לכאן מהרשימה.</div>
              ) : group.rows.map(a => (
                <div key={a._id} style={{display: 'flex', alignItems: 'center', gap: 12, padding: '10px 18px', borderTop: '1px solid #f6f8fb', flexWrap: 'wrap'}}>
                  <div style={{flex: '1 1 220px', minWidth: 180}}>
                    <input value={a.company_name || ''} onChange={e => patch(a._id, {company_name: e.target.value})} onBlur={e => saveField(a._id, {company_name: e.target.value})}
                      style={{width: '100%', border: '1px solid transparent', borderRadius: 6, padding: '4px 8px', fontSize: 14, fontWeight: 600, color: '#1e1e2d', background: 'transparent'}}
                      onFocus={e => e.currentTarget.style.border = '1px solid #e4e6ef'} title="שם החברה (לחיצה לעריכה)" />
                    <div style={{fontSize: 12, color: '#a1a5b7', paddingInlineStart: 8}}>
                      {a.agent_name || a.account?.first_name} {a.phone && <span dir="ltr"> · {a.phone}</span>}
                      {a.ai_confidence != null && <span> · AI {a.ai_confidence}%</span>}
                    </div>
                  </div>
                  <select value={(a.city || '').trim()} onChange={e => moveCity(a, e.target.value)}
                    style={{padding: '6px 10px', border: '1px solid #e4e6ef', borderRadius: 8, fontSize: 13, background: '#fff', minWidth: 130}}>
                    <option value="">{NO_CITY}</option>
                    {allCityOptions.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <button onClick={() => reAnalyze(a)} disabled={analyzing === a._id} title="נתח מחדש עם AI"
                    style={{padding: '6px 9px', borderRadius: 8, border: '1px solid #e4e6ef', background: '#fff', color: '#7e8299', cursor: 'pointer', fontSize: 13}}>
                    {analyzing === a._id ? <span className="spinner-border spinner-border-sm" style={{width: 12, height: 12}} /> : <i className="bi bi-robot" />}
                  </button>
                  <button onClick={() => exclude(a)} title="הסר מהרשימה"
                    style={{padding: '6px 9px', borderRadius: 8, border: '1px solid #fdd', background: '#fff5f8', color: '#f64e60', cursor: 'pointer', fontSize: 13}}>
                    <i className="bi bi-x-lg" />
                  </button>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default SalesAgentsPage
